import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import BarChart from "../../BarChart";
import HorizontalBarChart from "../../HorizontalBarChart";
import {
  SupplierOverviewResponse,
  SupplierByYear,
} from "../../../../types/suppliers";
import supplierTotal from "../../../../assets/images/supplierTotal.svg";
import supplierActive from "../../../../assets/images/supplierActive.svg";
import defaultActive from "../../../../assets/images/successImg.svg";
import supplierRegion from "../../../../assets/images/supplierRegion.svg";
import supplierRegister from "../../../../assets/images/supplieRegister.svg";
import statusDraft from "../../../../assets/images/statusDraft.svg";
import statusSubmitted from "../../../../assets/images/statusSubmitted.svg";
import statusPending from "../../../../assets/images/statusPending.svg";
import statusInProgress from "../../../../assets/images/statusInProgress.svg";
import statusApproved from "../../../../assets/images/statusApproved.svg";
import statusWarning from "../../../../assets/images/statusPending.svg";
import statusProtected from "../../../../assets/images/statusProtected.svg";
import styles from "./overviewDashboard.module.scss";

interface OverviewDashboardProps {
  overviewData: SupplierOverviewResponse;
}

interface CardConfig {
  key: string;
  icon: string;
}

const OverviewDashboard: React.FC<OverviewDashboardProps> = ({
  overviewData,
}) => {
  // Transform suppliersCountByYear to SupplierByYear[] format for BarChart
  const suppliersByYearData = useMemo((): SupplierByYear[] => {
    if (overviewData.suppliersCountByYear) {
      const { years, totalSuppliers, newSuppliers } =
        overviewData.suppliersCountByYear;
      return years.map((year, index) => ({
        year,
        total: totalSuppliers[index] || 0,
        new: newSuppliers[index] || 0,
      }));
    }
    return overviewData.suppliersByYear || [];
  }, [overviewData.suppliersCountByYear, overviewData.suppliersByYear]);
  const primaryCardConfigs: CardConfig[] = [
    { key: "totalSuppliers", icon: supplierTotal },
    { key: "activeSuppliers", icon: supplierActive },
    { key: "uaeRegionSuppliers", icon: supplierRegion },
    { key: "registeredSuppliersInSpecificYear", icon: supplierRegister },
  ];

  const primaryCards = useMemo(() => {
    if (!overviewData.primaryCards) return [];

    return primaryCardConfigs
      .map((config) => {
        const card =
          overviewData.primaryCards[
            config.key as keyof typeof overviewData.primaryCards
          ];
        return card ? { config, card } : null;
      })
      .filter(Boolean);
  }, [overviewData.primaryCards]);

  const renderPrimaryCards = () => {
    return primaryCards.map(({ config, card }, index) => (
      <Box key={index} className={styles.metricCard}>
        <Box className={styles.metricIconWrapper}>
          <Box className={styles.metricIcon}>
            <img src={config.icon} alt={config.key} />
          </Box>
        </Box>
        <Box className={styles.metricContent}>
          <Typography variant="h3" className={styles.metricValue}>
            {card.count}
          </Typography>
          <Typography variant="body2" className={styles.metricLabel}>
            {card.label}
          </Typography>
        </Box>
      </Box>
    ));
  };

  const getStatusIcon = (label: string): string => {
    const lowerLabel = label.toLowerCase();

    // Map status keywords to their respective icons
    if (lowerLabel.includes("draft")) return statusDraft;
    if (lowerLabel.includes("submitted")) return statusSubmitted;
    if (
      lowerLabel.includes("pending") ||
      lowerLabel.includes("pending_mapping")
    )
      return statusPending;
    if (
      lowerLabel.includes("evaluating") ||
      lowerLabel.includes("information_requested")
    )
      return statusInProgress;
    if (lowerLabel.includes("approved")) return statusApproved;
    if (lowerLabel.includes("inactive")) return statusWarning;
    if (lowerLabel.includes("onboarding")) return statusProtected;

    // Default fallback
    return defaultActive;
  };

  const getStatusColor = (label: string): string => {
    const lowerLabel = label.toLowerCase();

    // Map status keywords to colors matching the design
    if (lowerLabel.includes("approved")) return "#00D2AA"; // Teal/Cyan
    if (lowerLabel.includes("rejected")) return "#FF6B6B"; // Red/Pink
    if (lowerLabel.includes("draft")) return "#FFA500"; // Orange
    if (lowerLabel.includes("inactive")) return "#8B95B0"; // Gray/Blue
    if (lowerLabel.includes("onboarding")) return "#5B6B9E"; // Blue
    if (lowerLabel.includes("pending_mapping")) return "#A5B4FC"; // Light Purple
    if (lowerLabel.includes("information_requested")) return "#3B4976"; // Dark Navy
    if (lowerLabel.includes("pending_compliance")) return "#C7D2FE"; // Very Light Purple
    if (lowerLabel.includes("evaluating")) return "#7C3AED"; // Purple

    // Default fallback
    return "#5B6B9E";
  };

  const statusTypesWithColors = useMemo(() => {
    if (!overviewData.statusTypes) return [];

    return overviewData.statusTypes.map((status) => ({
      ...status,
      color: getStatusColor(status.label),
    }));
  }, [overviewData.statusTypes]);

  const renderStatusTypes = () => {
    if (!overviewData.statusTypes || overviewData.statusTypes.length === 0) {
      return null;
    }

    // Display all status types
    return overviewData.statusTypes.map((statusType, index) => {
      const icon = getStatusIcon(statusType.label);
      const iconColor = "#F9FAFB";

      return (
        <Box key={index} className={styles.secondaryMetricCard}>
          <Box
            className={styles.secondaryMetricIcon}
            style={{ backgroundColor: iconColor }}
          >
            <img src={icon} alt="status icon" />
          </Box>
          <Box className={styles.secondaryMetricContent}>
            <Typography variant="h5" className={styles.secondaryMetricValue}>
              {statusType.count}
            </Typography>
            <Typography variant="body2" className={styles.secondaryMetricLabel}>
              {statusType.label}
            </Typography>
          </Box>
        </Box>
      );
    });
  };

  // Dynamic grid columns based on card count with responsive behavior
  const getDynamicGridStyles = () => {
    const count = primaryCards.length;

    return {
      gridTemplateColumns: {
        xs: '1fr', // Mobile: 1 column
        sm: count === 1 ? '1fr' : 'repeat(2, 1fr)', // Tablet: 2 columns (or 1 if only 1 card)
        md: count <= 2 ? `repeat(${count}, 1fr)` : 'repeat(3, 1fr)', // Desktop: dynamic
        lg: `repeat(${Math.min(count, 4)}, 1fr)`, // Large: up to 4 columns
      },
    };
  };

  return (
    <>
      <Box
        className={styles.primaryMetrics}
        sx={getDynamicGridStyles()}
      >
        {renderPrimaryCards()}
      </Box>

      <Box className={styles.secondaryMetrics}>{renderStatusTypes()}</Box>

      <Box className={styles.fullWidthChart}>
        <BarChart
          title="New Suppliers Count by Year"
          data={suppliersByYearData}
          xAxisKey="year"
          bars={[
            // { dataKey: "total", color: "#455391", label: "Total Suppliers" },
            { dataKey: "new", color: "#00D2AA", label: "New Suppliers" },
          ]}
        />
        {/* <HorizontalBarChart
          title="Due Diligence Status"
          data={statusTypesWithColors}
          nameKey="label"
          valueKey="count"
          colorKey="color"
          valueLabel="suppliers"
        /> */}
      </Box>
    </>
  );
};

export default OverviewDashboard;
